<template>
  <div
    class="w-full overflow-hidden rounded-2xl border border-slate-800 bg-slate-950 text-white shadow-2xl transition-all duration-300"
  >
    <!-- Header Controls Bar -->
    <div
      class="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 border-b border-slate-800/80 bg-slate-900/90 px-3.5 sm:px-4 py-3 backdrop-blur-md"
    >
      <!-- Title & Mac Dots -->
      <div
        class="flex items-center space-x-2 w-full sm:w-auto justify-between sm:justify-start"
      >
        <div class="flex items-center space-x-1.5">
          <div class="h-2.5 w-2.5 rounded-full bg-rose-500/80"></div>
          <div class="h-2.5 w-2.5 rounded-full bg-amber-500/80"></div>
          <div class="h-2.5 w-2.5 rounded-full bg-emerald-500/80"></div>
          <span class="ml-2 text-xs font-semibold tracking-wide text-slate-300">
            Live Website Simulator
          </span>
        </div>

        <!-- Live Status Indicator -->
        <span
          class="inline-flex sm:hidden items-center gap-1.5 px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-400 text-[10px] font-medium border border-emerald-500/20"
        >
          <span
            class="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse"
          ></span>
          Live
        </span>
      </div>

      <!-- Controls Group -->
      <div
        class="flex items-center justify-between sm:justify-end gap-2 w-full sm:w-auto"
      >
        <!-- Autoplay Button -->
        <button
          type="button"
          class="rounded-lg border border-slate-800 bg-slate-950 px-2.5 py-1.5 text-[11px] sm:text-xs font-medium text-indigo-400 hover:text-indigo-300 hover:border-slate-700 transition active:scale-95 cursor-pointer flex items-center gap-1.5"
          @click="toggleAutoplayPreview"
        >
          <span
            class="h-1.5 w-1.5 rounded-full"
            :class="
              isAutoPlaying ? 'bg-indigo-400 animate-pulse' : 'bg-slate-600'
            "
          ></span>
          {{ isAutoPlaying ? "Pause" : "Play" }}
        </button>

        <!-- Viewport Mode Switcher -->
        <div
          class="flex items-center rounded-lg border border-slate-800 bg-slate-950 p-0.5"
        >
          <button
            type="button"
            class="flex items-center space-x-1 rounded-md px-2.5 py-1 text-[11px] sm:text-xs font-semibold transition cursor-pointer"
            :class="
              viewport === 'desktop'
                ? 'bg-indigo-600 text-white shadow-xs'
                : 'text-slate-400 hover:text-white'
            "
            @click="viewport = 'desktop'"
          >
            <MonitorIcon class="h-3.5 w-3.5" />
            <span class="hidden xs:inline">Desktop</span>
          </button>
          <button
            type="button"
            class="flex items-center space-x-1 rounded-md px-2.5 py-1 text-[11px] sm:text-xs font-semibold transition cursor-pointer"
            :class="
              viewport === 'mobile'
                ? 'bg-indigo-600 text-white shadow-xs'
                : 'text-slate-400 hover:text-white'
            "
            @click="viewport = 'mobile'"
          >
            <SmartphoneIcon class="h-3.5 w-3.5" />
            <span class="hidden xs:inline">Mobile</span>
          </button>
        </div>
      </div>
    </div>

    <!-- Simulator Canvas Area -->
    <div
      class="relative flex min-h-[460px] items-center justify-center overflow-x-auto bg-slate-900/40 p-3 sm:p-6"
    >
      <div
        class="relative overflow-hidden rounded-2xl border border-slate-800 bg-[#1B2A4A] shadow-2xl transition-all duration-300"
        :style="containerStyles"
      >
        <!-- Top Metadata Bar Simulator -->
        <div
          class="flex items-center justify-between border-b border-white/10 bg-[#121f3b] px-3.5 py-1.5 text-[10px] text-slate-300"
        >
          <div class="flex items-center gap-2 truncate">
            <span class="font-bold text-[#C99A3B]"
              >Estd. {{ headerMeta.establishmentYear || "1985" }}</span
            >
            <span class="text-white/20">•</span>
            <span class="truncate">{{
              headerMeta.boardAffiliation || "CBSE Affiliated"
            }}</span>
            <span
              v-if="headerMeta.affiliationNumber"
              class="hidden sm:inline text-slate-400"
              >(#{{ headerMeta.affiliationNumber }})</span
            >
          </div>
          <div class="hidden sm:flex items-center gap-2 text-slate-400">
            <i class="far fa-clock text-[9px] text-[#C99A3B]"></i>
            <span>{{ headerMeta.officeHours || "Mon–Sat 8am–3pm" }}</span>
          </div>
        </div>

        <div
          v-if="activeSlide"
          class="relative h-[calc(100%-28px)] w-full"
          :class="viewport === 'mobile' ? 'flex flex-col' : 'grid grid-cols-12'"
        >
          <!-- LEFT / BOTTOM: COPY & BUTTONS -->
          <div
            class="flex flex-col justify-center text-white z-10"
            :class="
              viewport === 'mobile'
                ? 'order-2 w-full p-4'
                : 'col-span-6 p-6 sm:p-8'
            "
          >
            <span
              class="text-[10px] font-bold uppercase tracking-widest text-[#C99A3B]"
            >
              {{ activeSlide.badge || "ADMISSIONS OPEN 2026-27" }}
            </span>

            <h2
              class="mt-1.5 font-serif font-extrabold tracking-tight text-white leading-tight"
              :class="
                viewport === 'mobile'
                  ? 'text-base sm:text-lg'
                  : 'text-xl sm:text-2xl'
              "
            >
              {{ activeSlide.title || "Slide Title Here" }}
            </h2>

            <p class="mt-2 text-xs leading-relaxed text-[#C7CEE0] line-clamp-3">
              {{
                activeSlide.description ||
                "Slide description paragraph goes here..."
              }}
            </p>

            <!-- Highlights List -->
            <ul
              v-if="highlights && highlights.length"
              class="mt-3 flex flex-wrap gap-1.5"
            >
              <li
                v-for="(hl, hIdx) in highlights"
                :key="hIdx"
                class="inline-flex items-center gap-1 rounded-full border border-white/15 bg-white/5 px-2 py-0.5 text-[10px] text-slate-200"
              >
                <i
                  :class="[
                    'fas',
                    hl.icon || 'fa-circle-check',
                    'text-[9px] text-[#C99A3B]',
                  ]"
                ></i>
                <span>{{ hl.text }}</span>
              </li>
            </ul>

            <!-- Buttons -->
            <div class="mt-4 sm:mt-5 flex flex-wrap items-center gap-2">
              <a
                v-if="activeSlide.buttonText"
                href="javascript:void(0)"
                class="rounded-md bg-[#C99A3B] px-3.5 py-2 text-xs font-bold text-[#1B2A4A] shadow-sm hover:bg-[#d8a846] transition active:scale-95"
              >
                {{ activeSlide.buttonText }}
              </a>
              <a
                v-if="activeSlide.secondaryButtonText"
                href="javascript:void(0)"
                class="rounded-md border border-white/30 bg-white/5 px-3.5 py-2 text-xs font-bold text-white hover:bg-white/10 transition active:scale-95"
              >
                {{ activeSlide.secondaryButtonText }}
              </a>
            </div>

            <!-- Stats -->
            <div
              v-if="stats && stats.length"
              class="mt-4 flex items-center gap-4 border-t border-white/10 pt-3 text-[10px] text-slate-300"
            >
              <div
                v-for="(st, sIdx) in stats"
                :key="sIdx"
              >
                <strong class="block text-xs sm:text-sm text-white font-bold">{{
                  st.number
                }}</strong
                >{{ (st.label || "").toUpperCase() }}
              </div>
            </div>
          </div>

          <!-- RIGHT / TOP: PHOTO CONTAINER -->
          <div
            class="relative overflow-hidden bg-slate-900"
            :class="
              viewport === 'mobile'
                ? 'order-1 h-[200px] w-full'
                : 'col-span-6 h-full'
            "
          >
            <!-- Image Renderer -->
            <img
              v-if="resolvedImageUrl"
              :src="resolvedImageUrl"
              alt="Hero Preview Image"
              class="h-full w-full object-cover transition-all duration-500"
              @error="handleImageError"
            />

            <!-- Fallback State -->
            <div
              v-else
              class="flex h-full w-full flex-col items-center justify-center p-4 text-center bg-slate-900/80 text-xs text-slate-400"
            >
              <ImageIcon class="mb-2 h-7 w-7 text-slate-600" />
              <span>No Valid Image Found</span>
            </div>

            <!-- Overlay Layer -->
            <div
              class="absolute inset-0 bg-black pointer-events-none transition-opacity duration-300"
              :style="{ opacity: overlayOpacityCalculated }"
            ></div>

            <!-- Caption -->
            <div
              v-if="activeSlide.caption"
              class="absolute bottom-3 left-3 rounded-md bg-[#F6F2E7]/95 backdrop-blur-xs px-2.5 py-1 text-[10px] font-bold text-[#1B2A4A] shadow-md z-10 flex items-center"
            >
              <span
                class="mr-1.5 inline-block h-1.5 w-1.5 rounded-full bg-[#B23A34]"
              ></span>
              {{ activeSlide.caption }}
            </div>

            <!-- Navigation Dots -->
            <div
              v-if="settings.showDots !== false && activeSlides.length > 1"
              class="absolute top-3 left-3 flex space-x-1.5 z-10"
            >
              <button
                v-for="(s, idx) in activeSlides"
                :key="idx"
                type="button"
                class="h-1.5 rounded-full transition-all cursor-pointer"
                :class="
                  idx === currentIndex
                    ? 'w-4 bg-[#C99A3B]'
                    : 'w-1.5 bg-white/50'
                "
                @click="currentIndex = idx"
              ></button>
            </div>

            <!-- Arrows -->
            <div
              v-if="settings.showArrows !== false && activeSlides.length > 1"
              class="absolute bottom-3 right-3 flex space-x-1.5 z-10"
            >
              <button
                type="button"
                class="flex h-6 w-6 items-center justify-center rounded-full bg-black/40 text-white backdrop-blur-md hover:bg-[#C99A3B] hover:text-black transition active:scale-90 cursor-pointer"
                @click="prevSlide"
              >
                <ChevronLeftIcon class="h-3.5 w-3.5" />
              </button>
              <button
                type="button"
                class="flex h-6 w-6 items-center justify-center rounded-full bg-black/40 text-white backdrop-blur-md hover:bg-[#C99A3B] hover:text-black transition active:scale-90 cursor-pointer"
                @click="nextSlide"
              >
                <ChevronRightIcon class="h-3.5 w-3.5" />
              </button>
            </div>
          </div>
        </div>

        <!-- No Slides Fallback -->
        <div
          v-else
          class="flex h-full w-full flex-col items-center justify-center p-8 text-slate-500"
        >
          <ImageIcon class="mb-2 h-10 w-10 text-slate-700" />
          <p class="text-xs">No active slides found.</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
  import { ref, computed, watch, onUnmounted } from "vue";
  import {
    Monitor as MonitorIcon,
    Smartphone as SmartphoneIcon,
    ChevronLeft as ChevronLeftIcon,
    ChevronRight as ChevronRightIcon,
    Image as ImageIcon,
  } from "lucide-vue-next";

  const props = defineProps({
    settings: { type: Object, default: () => ({}) },
    slides: { type: Array, default: () => [] },
    stats: { type: Array, default: () => [] },
    highlights: { type: Array, default: () => [] },
    headerMeta: { type: Object, default: () => ({}) },
  });

  const viewport = ref("desktop");
  const currentIndex = ref(0);
  const imageFailed = ref(false);
  const isAutoPlaying = ref(false);
  let autoPlayTimer = null;
  const objectUrlMap = new Map();

  const activeSlides = computed(() =>
    (props.slides || []).filter((s) => s && s.active !== false),
  );

  const activeSlide = computed(() => {
    if (!activeSlides.value.length) return null;
    if (currentIndex.value >= activeSlides.value.length) {
      // eslint-disable-next-line vue/no-side-effects-in-computed-properties
      currentIndex.value = 0;
    }
    return activeSlides.value[currentIndex.value] || activeSlides.value[0];
  });

  const containerStyles = computed(() => {
    if (viewport.value === "mobile")
      return { width: "320px", minHeight: "500px" };
    let h = "450px";
    if (props.settings?.heroHeight === "Small") h = "380px";
    if (props.settings?.heroHeight === "Large") h = "500px";
    if (props.settings?.heroHeight === "Fullscreen") h = "540px";
    return { width: "100%", maxWidth: "920px", height: h };
  });

  const overlayOpacityCalculated = computed(() => {
    if (!activeSlide.value) return 0.2;
    const op = activeSlide.value.overlayOpacity;
    if (op === undefined || op === null || isNaN(op)) return 0.2;
    return Math.min(Math.max(Number(op), 0), 0.85);
  });

  const resolvedImageUrl = computed(() => {
    if (imageFailed.value || !activeSlide.value) return "";

    let rawMedia = activeSlide.value.image;
    if (viewport.value === "mobile" && activeSlide.value.mobileImage) {
      rawMedia = activeSlide.value.mobileImage;
    }

    if (!rawMedia) return "";

    if (typeof rawMedia === "string") return rawMedia;

    if (rawMedia instanceof File) {
      if (!objectUrlMap.has(rawMedia)) {
        objectUrlMap.set(rawMedia, URL.createObjectURL(rawMedia));
      }
      return objectUrlMap.get(rawMedia);
    }

    if (typeof rawMedia === "object") {
      return (
        rawMedia.url ||
        rawMedia.secure_url ||
        rawMedia.src ||
        rawMedia.path ||
        ""
      );
    }

    return "";
  });

  const handleImageError = () => {
    imageFailed.value = true;
  };

  const nextSlide = () => {
    if (!activeSlides.value.length) return;
    imageFailed.value = false;
    if (props.settings.infiniteLoop !== false) {
      currentIndex.value = (currentIndex.value + 1) % activeSlides.value.length;
    } else if (currentIndex.value < activeSlides.value.length - 1) {
      currentIndex.value++;
    }
  };

  const prevSlide = () => {
    if (!activeSlides.value.length) return;
    imageFailed.value = false;
    if (props.settings.infiniteLoop !== false) {
      currentIndex.value =
        (currentIndex.value - 1 + activeSlides.value.length) %
        activeSlides.value.length;
    } else if (currentIndex.value > 0) {
      currentIndex.value--;
    }
  };

  const startAutoplay = () => {
    stopAutoplay();
    if (props.settings.autoplay !== false && activeSlides.value.length > 1) {
      isAutoPlaying.value = true;
      autoPlayTimer = setInterval(
        nextSlide,
        props.settings.autoplayDelay || 5000,
      );
    }
  };

  const stopAutoplay = () => {
    isAutoPlaying.value = false;
    if (autoPlayTimer) {
      clearInterval(autoPlayTimer);
      autoPlayTimer = null;
    }
  };

  const toggleAutoplayPreview = () => {
    if (isAutoPlaying.value) stopAutoplay();
    else startAutoplay();
  };

  watch(
    [
      () => props.settings.autoplay,
      () => props.settings.autoplayDelay,
      () => props.settings.infiniteLoop,
      activeSlides,
    ],
    () => {
      startAutoplay();
    },
    { immediate: true },
  );

  watch(
    () => currentIndex.value,
    () => {
      imageFailed.value = false;
    },
  );

  onUnmounted(() => {
    stopAutoplay();
    objectUrlMap.forEach((url) => URL.revokeObjectURL(url));
    objectUrlMap.clear();
  });
</script>
